/**
 * ProposalReview Component
 *
 * Version History:
 * v3.18 - BATCH-2026-10-31B - the impact field is laymanExplanation (the name Stage 5 writes) - 2026-10-31
 * v3.17 - BATCH-2026-10-31B (Branko ruling 2026-10-31) - 2026-10-31: each waiting step shows the short plain-business
 *                       impact text Stage 5 stores on the proposal (one accessor, impactExplanationOf, over one field
 *                       name, IMPACT_EXPLANATION_FIELD). No text is typed here: when the field is absent nothing extra is
 *                       shown (the API logs the missing ones).
 * v3.16 - 2026-03-09 - SAP US12561297B2 Escape: Added needs_review card variant in renderStandaloneCard.
 *                       Proposals with status 'needs_review' (adversarial Generator-Critic loop exhausted
 *                       MAX_RETRIES below CONFIDENCE_THRESHOLD) now render an amber-bordered card exposing
 *                       the generator instruction (operation, columns, rationale) and reviewer critique
 *                       (confidence score, concerns, justification) with Apply/Skip action buttons.
 *                       Also updated Proposal.status union type in api.ts to include 'needs_review' and
 *                       added aiDecision field with instruction/confidence/concerns/justification.
 * v3.15 - 2026-02-15 - Fix #7: Removed "All high-risk — review individually" chip completely.
 *                       Fix #4: Bulk actions (Reject All, Accept All) at category/subcategory level
 *                       only clear local decisions for the affected scope, preserving decisions in
 *                       other categories. Fixes lost approvals when acting across categories.
 * v3.10 - 2026-02-14 - Fixed lost selections after 500: moved setPendingDecisions({}) to after
 *                       both batch commit AND resume succeed. Dynamic need-review counts at
 *                       category/subcategory level decrement on local approve/reject/undo.
 *                       Made "All high-risk" chip non-interactive. Disabled sub-level Reject All
 *                       when all items locally decided.
 * v3.09 - 2026-02-14 - Batch-commit UX: approve/reject stored locally, cards update instantly
 *                       with Undo support. Only "Continue Setup" sends batch to server.
 *                       Fixed "Approved 0" on all-high-risk subcategories.
 * v3.08 - 2026-02-12 - Fixed 500 on Change Strategy (route ordering), clickable handled/pending
 *                       chips on subcategories, status_filter for subcategory endpoint,
 *                       handled items read-only view, auto-show handled when no pending.
 * v3.07 - 2026-02-11 - Bulk actions: Reject All globally, Accept/Reject All at category level,
 *                       Reject Remaining at subcategory level, clear high-risk feedback after
 *                       Approve All Recommendations.
 * v3.06 - 2026-02-11 - Semantic Strategy Selection: "Change" button on proposals opens
 *                       alternatives dialog, override endpoint updates strategy with audit trail.
 * v3.05 - 2026-02-11 - Lazy-loading rewrite: L1 collapsed categories, L2 nested subcategory
 *                       accordions with "Accept Recommendations", L3 lazy-loaded on expand.
 *                       Summary returns counts only (no proposal objects on initial load).
 * v3.04 - 2026-02-11 - Summary-based hierarchical review with decision bundles (superseded)
 * v3.03 - 2026-02-11 - Hierarchical grouped view (superseded)
 * v3.00 - 2026-02-07 - Reject All button, change decisions on review screen
 * v2.90 - 2026-02-06 - Fallback chains for NB05 raw field names
 * v2.88 - 2026-02-06 - Initial implementation for proposal approval flow
 *
 * Architecture: "Load 20 summary rows, fetch details on demand."
 * - Page load: GET /api/proposals/summary → counts only
 * - L1: Category accordions (collapsed by default)
 * - L2: Subcategory nested accordions + "Accept Recommendations" button
 * - L3: Lazy-loaded on L2 expand via GET /api/proposals/subcategory
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormControlLabel,
  Snackbar,
  Divider,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  proposalService,
  setupService,
  ProposalSummaryResponse,
  CategorySummary,
  SubcategorySummary,
  SubcategoryDetailResponse,
  DecisionBundle,
  Proposal,
  AlternativeStrategy,
} from '../services/api';

// v3.17 - BATCH-2026-10-31B: the ONE place the impact field is named (matches approval_api.py IMPACT_EXPLANATION_FIELD).
// v3.18 - BATCH-2026-10-31B - B1 confirmed the field Stage 5 writes: laymanExplanation (top level) - 2026-10-31
const IMPACT_EXPLANATION_FIELD = 'laymanExplanation';

// v3.17 - the proposal's impact text, or null when absent or empty (never a fallback text)
const impactExplanationOf = (proposal: unknown): string | null => {
  if (!proposal || typeof proposal !== 'object') return null;
  const value = (proposal as Record<string, unknown>)[IMPACT_EXPLANATION_FIELD];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

// v3.17 - one small line under the step's existing text; renders nothing without the field
const ImpactLine: React.FC<{ proposal: unknown; indent?: boolean }> = ({ proposal, indent }) => {
  const impact = impactExplanationOf(proposal);
  if (!impact) return null;
  return (
    <Typography variant="body2" sx={{ display: 'block', mt: 0.5, ml: indent ? 4 : 0 }}>
      {impact}
    </Typography>
  );
};

interface ProposalReviewProps {
  onResume: () => void;
}

// v3.09 - Local decision tracking for batch-commit UX
interface LocalDecision {
  action: 'approve' | 'reject';
  reason?: string;
  bundleGroupKey?: string;
  supersede?: boolean; // non-selected bundle alternatives
}

// v3.05 - Risk level color mapping
const riskColors: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'success',
};

const ProposalReview: React.FC<ProposalReviewProps> = ({ onResume }) => {
  // Summary (counts only)
  const [summary, setSummary] = useState<ProposalSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // L1: Category accordions — ALL collapsed by default
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // L2: Subcategory accordions — collapsed by default
  const [expandedSubcategories, setExpandedSubcategories] = useState<Record<string, boolean>>({});

  // L3: Lazy-loaded subcategory data cache
  const [subcategoryData, setSubcategoryData] = useState<Record<string, SubcategoryDetailResponse>>({});
  const [subcategoryLoading, setSubcategoryLoading] = useState<Record<string, boolean>>({});
  // v3.07 - Ref tracks loaded cache keys to avoid stale closure in loadSubcategory
  const subcategoryCacheRef = useRef<Set<string>>(new Set());
  // v3.24 - Ref for auto-scroll to "Continue Setup" when all proposals handled
  const continueSetupRef = useRef<HTMLDivElement>(null);
  // v3.08 - Track active filter per subcategory: "pending" or "handled"
  const [subcategoryFilter, setSubcategoryFilter] = useState<Record<string, string>>({});

  // Bundle selections (radio group state)
  const [bundleSelections, setBundleSelections] = useState<Record<string, string>>({});

  // Action states
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  // v3.07 - Added 'info' severity for high-risk feedback message
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success',
  });
  const [resuming, setResuming] = useState(false);

  // v3.06 - Strategy override dialog state
  const [overrideDialog, setOverrideDialog] = useState<{
    open: boolean;
    proposalId: string;
    catKey: string;
    subKey: string;
    currentStrategy: string;
    alternatives: AlternativeStrategy[];
    loading: boolean;
  }>({
    open: false, proposalId: '', catKey: '', subKey: '',
    currentStrategy: '', alternatives: [], loading: false,
  });

  // v3.09 - Local decisions for batch-commit UX (no API call until Continue Setup)
  const [pendingDecisions, setPendingDecisions] = useState<Record<string, LocalDecision>>({});

  // v3.09 - Count locally decided items (exclude superseded bundle alternatives from count)
  const locallyDecidedCount = useMemo(() => {
    let count = 0;
    Object.values(pendingDecisions).forEach(d => {
      if (!d.supersede) count++;
    });
    return count;
  }, [pendingDecisions]);

  // v3.10 - Per-subcategory local decision counts for dynamic need-review updates
  // Maps "catKey:subKey" → number of non-superseded local decisions in that subcategory
  const localCountsBySubcategory = useMemo(() => {
    const counts: Record<string, number> = {};
    // Walk loaded subcategory data to map proposal IDs to their subcategory
    Object.entries(subcategoryData).forEach(([displayKey, detail]) => {
      // Only count display keys (catKey:subKey), skip cache keys (catKey:subKey:filter)
      if (displayKey.split(':').length !== 2) return;
      let subCount = 0;
      detail.standalone.forEach(p => {
        const d = pendingDecisions[p.id];
        if (d && !d.supersede) subCount++;
      });
      detail.decisionBundles.forEach(b => {
        // Count a bundle as 1 decision if all alternatives are locally decided
        const allDecided = b.alternatives.every(alt => pendingDecisions[alt.id]);
        if (allDecided) subCount++;
      });
      counts[displayKey] = subCount;
    });
    return counts;
  }, [pendingDecisions, subcategoryData]);

  // v3.09 - Navigation warning for unsaved decisions
  useEffect(() => {
    const hasUnsaved = Object.keys(pendingDecisions).length > 0;
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    if (hasUnsaved) {
      window.addEventListener('beforeunload', handler);
    }
    return () => window.removeEventListener('beforeunload', handler);
  }, [pendingDecisions]);

  // Load summary (counts only)
  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await proposalService.getSummary();
      setSummary(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load proposal summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // v3.24 - Auto-scroll to "Continue Setup" when all proposals have been handled
  useEffect(() => {
    if (summary && locallyDecidedCount > 0) {
      const effectiveRemaining = Math.max(0, (summary.totalNeedsReview || 0) - locallyDecidedCount);
      if (effectiveRemaining === 0 && continueSetupRef.current) {
        setTimeout(() => {
          continueSetupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }
  }, [locallyDecidedCount, summary]);

  // v3.07 - Lazy load subcategory proposals on L2 expand (force=true skips cache for reload after actions)
  // v3.08 - Added statusFilter param to load pending or handled items
  const loadSubcategory = useCallback(async (catKey: string, subKey: string, force = false, statusFilter = 'pending') => {
    const cacheKey = `${catKey}:${subKey}:${statusFilter}`;

    // Use ref for cache check to avoid stale closure
    if (!force && subcategoryCacheRef.current.has(cacheKey)) {
      // Still update the display key even if cached
      const displayKey = `${catKey}:${subKey}`;
      setSubcategoryData(prev => {
        const cached = prev[cacheKey];
        if (cached) return { ...prev, [displayKey]: cached };
        return prev;
      });
      return;
    }

    const displayKey = `${catKey}:${subKey}`;
    setSubcategoryLoading(prev => ({ ...prev, [displayKey]: true }));
    try {
      const data = await proposalService.getSubcategoryProposals(catKey, subKey, statusFilter);
      setSubcategoryData(prev => ({ ...prev, [displayKey]: data, [cacheKey]: data }));
      subcategoryCacheRef.current.add(cacheKey);

      // Initialize bundle selections to recommended IDs (only for pending)
      if (statusFilter === 'pending') {
        const selections: Record<string, string> = {};
        data.decisionBundles.forEach((bundle) => {
          if (bundle.recommendedId) {
            selections[bundle.groupKey] = bundle.recommendedId;
          } else if (bundle.alternatives.length > 0) {
            selections[bundle.groupKey] = bundle.alternatives[0].id;
          }
        });
        setBundleSelections(prev => ({ ...prev, ...selections }));
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || `Failed to load ${subKey} proposals`,
        severity: 'error',
      });
    } finally {
      setSubcategoryLoading(prev => ({ ...prev, [displayKey]: false }));
    }
  }, []); // v3.07 - No state dependencies, uses ref for cache check

  // Handle L2 subcategory accordion toggle
  // v3.08 - Auto-detect filter: if no pending items, show handled by default
  const handleSubcategoryToggle = (catKey: string, subKey: string, sub?: SubcategorySummary) => {
    const expandKey = `${catKey}:${subKey}`;
    const isExpanding = !expandedSubcategories[expandKey];
    setExpandedSubcategories(prev => ({ ...prev, [expandKey]: isExpanding }));

    if (isExpanding) {
      // v3.08 - If no pending items, auto-load handled view
      const needsReview = sub?.needsReviewCount ?? 0;
      const defaultFilter = needsReview === 0 ? 'handled' : 'pending';
      const currentFilter = subcategoryFilter[expandKey] || defaultFilter;
      setSubcategoryFilter(prev => ({ ...prev, [expandKey]: currentFilter }));
      loadSubcategory(catKey, subKey, false, currentFilter);
    }
  };

  // v3.08 - Switch between pending/handled view for a subcategory
  const handleFilterSwitch = (catKey: string, subKey: string, newFilter: string) => {
    const expandKey = `${catKey}:${subKey}`;
    setSubcategoryFilter(prev => ({ ...prev, [expandKey]: newFilter }));
    loadSubcategory(catKey, subKey, false, newFilter);
  };

  // v3.07 - Global "Approve All Recommendations" (low+medium risk only) with high-risk feedback
  const handleApproveAllRecommendations = async () => {
    setActionLoading('approve_all');
    try {
      const result = await proposalService.bulkUpdate({
        filter_type: 'recommended_pending',
        action: 'approve',
      });
      // v3.15 - Fix #4: Clear ALL local decisions (global action covers all categories)
      setPendingDecisions({});
      // Invalidate all L3 caches and collapse subcategories
      setSubcategoryData({});
      subcategoryCacheRef.current.clear();
      setExpandedSubcategories({});
      // Reload summary to get updated counts
      const updatedSummary = await proposalService.getSummary();
      setSummary(updatedSummary);
      // v3.07 - Show contextual feedback: if items remain, explain they are high-risk
      if (updatedSummary.totalNeedsReview > 0) {
        setSnackbar({
          open: true,
          message: `Approved ${result.updated} proposals. ${updatedSummary.totalNeedsReview} high-risk items remain. Review individually or click "Reject All Remaining".`,
          severity: 'info',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Approved ${result.updated} recommended proposals. All done!`,
          severity: 'success',
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to approve recommendations',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // v3.05 - Per-subcategory "Accept Recommendations" (low+medium risk only)
  const handleAcceptSubcategoryRecommendations = async (catKey: string, subKey: string) => {
    const actionKey = `accept_${catKey}:${subKey}`;
    setActionLoading(actionKey);
    try {
      const result = await proposalService.bulkUpdate({
        filter_type: 'subcategory',
        filter_value: `${catKey}:${subKey}`,
        action: 'approve',
      });
      // v3.15 - Fix #4: Only clear local decisions for THIS subcategory, preserve others
      setPendingDecisions(prev => {
        const next = { ...prev };
        const displayKey = `${catKey}:${subKey}`;
        const detail = subcategoryData[displayKey];
        if (detail) {
          detail.standalone.forEach(p => delete next[p.id]);
          detail.decisionBundles.forEach(b => b.alternatives.forEach(a => delete next[a.id]));
        }
        return next;
      });
      setSnackbar({
        open: true,
        message: `Approved ${result.updated} recommendations`,
        severity: 'success',
      });
      // Collapse L3 + invalidate cache for this subcategory (Option A: stale prevention)
      const cacheKey = `${catKey}:${subKey}`;
      subcategoryCacheRef.current.delete(cacheKey);
      setExpandedSubcategories(prev => ({ ...prev, [cacheKey]: false }));
      setSubcategoryData(prev => {
        const { [cacheKey]: _, ...rest } = prev;
        return rest;
      });
      await loadSummary();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to accept recommendations',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // v3.07 - Global "Reject All Remaining" (all pending, any risk level)
  const handleRejectAllRemaining = async () => {
    setActionLoading('reject_all');
    try {
      const result = await proposalService.bulkUpdate({
        filter_type: 'all_pending',
        action: 'reject',
        reason: 'Bulk rejected by user',
      });
      // v3.15 - Fix #4: Clear ALL local decisions (global action covers everything)
      setPendingDecisions({});
      setSnackbar({
        open: true,
        message: `Rejected ${result.updated} remaining proposals`,
        severity: 'success',
      });
      setSubcategoryData({});
      subcategoryCacheRef.current.clear();
      setExpandedSubcategories({});
      await loadSummary();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to reject remaining proposals',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // v3.07 - Category-level "Accept All" (approve recommended low+medium in category)
  const handleCategoryAcceptAll = async (catKey: string) => {
    const actionKey = `cat_accept_${catKey}`;
    setActionLoading(actionKey);
    try {
      const result = await proposalService.bulkUpdate({
        filter_type: 'category_pending',
        filter_value: catKey,
        action: 'approve',
      });
      // v3.15 - Fix #4: Only clear local decisions for THIS category, preserve other categories
      setPendingDecisions(prev => {
        const next = { ...prev };
        Object.keys(subcategoryData).forEach(key => {
          if (key.startsWith(`${catKey}:`) && key.split(':').length === 2) {
            const detail = subcategoryData[key];
            if (detail) {
              detail.standalone.forEach(p => delete next[p.id]);
              detail.decisionBundles.forEach(b => b.alternatives.forEach(a => delete next[a.id]));
            }
          }
        });
        return next;
      });
      setSnackbar({
        open: true,
        message: `Approved ${result.updated} proposals in ${catKey.replace(/_/g, ' ')}`,
        severity: 'success',
      });
      // Invalidate all subcategory caches in this category
      setSubcategoryData(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${catKey}:`)) {
            delete updated[key];
            subcategoryCacheRef.current.delete(key);
          }
        });
        return updated;
      });
      setExpandedSubcategories(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${catKey}:`)) updated[key] = false;
        });
        return updated;
      });
      await loadSummary();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to accept category proposals',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // v3.07 - Category-level "Reject All" (reject all pending in category, any risk)
  const handleCategoryRejectAll = async (catKey: string) => {
    const actionKey = `cat_reject_${catKey}`;
    setActionLoading(actionKey);
    try {
      const result = await proposalService.bulkUpdate({
        filter_type: 'category_pending',
        filter_value: catKey,
        action: 'reject',
        reason: 'Bulk rejected by user at category level',
      });
      // v3.15 - Fix #4: Only clear local decisions for THIS category, preserve other categories
      setPendingDecisions(prev => {
        const next = { ...prev };
        Object.keys(subcategoryData).forEach(key => {
          if (key.startsWith(`${catKey}:`) && key.split(':').length === 2) {
            const detail = subcategoryData[key];
            if (detail) {
              detail.standalone.forEach(p => delete next[p.id]);
              detail.decisionBundles.forEach(b => b.alternatives.forEach(a => delete next[a.id]));
            }
          }
        });
        return next;
      });
      setSnackbar({
        open: true,
        message: `Rejected ${result.updated} proposals in ${catKey.replace(/_/g, ' ')}`,
        severity: 'success',
      });
      setSubcategoryData(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${catKey}:`)) {
            delete updated[key];
            subcategoryCacheRef.current.delete(key);
          }
        });
        return updated;
      });
      setExpandedSubcategories(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${catKey}:`)) updated[key] = false;
        });
        return updated;
      });
      await loadSummary();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to reject category proposals',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // v3.07 - Subcategory-level "Reject Remaining" (reject all pending in subcategory, any risk)
  const handleRejectSubcategoryRemaining = async (catKey: string, subKey: string) => {
    const actionKey = `sub_reject_${catKey}:${subKey}`;
    setActionLoading(actionKey);
    try {
      const result = await proposalService.bulkUpdate({
        filter_type: 'subcategory_pending',
        filter_value: `${catKey}:${subKey}`,
        action: 'reject',
        reason: 'Bulk rejected by user at subcategory level',
      });
      // v3.15 - Fix #4: Only clear local decisions for THIS subcategory, preserve others
      setPendingDecisions(prev => {
        const next = { ...prev };
        const displayKey = `${catKey}:${subKey}`;
        const detail = subcategoryData[displayKey];
        if (detail) {
          detail.standalone.forEach(p => delete next[p.id]);
          detail.decisionBundles.forEach(b => b.alternatives.forEach(a => delete next[a.id]));
        }
        return next;
      });
      setSnackbar({
        open: true,
        message: `Rejected ${result.updated} remaining proposals`,
        severity: 'success',
      });
      const cacheKey = `${catKey}:${subKey}`;
      subcategoryCacheRef.current.delete(cacheKey);
      setExpandedSubcategories(prev => ({ ...prev, [cacheKey]: false }));
      setSubcategoryData(prev => {
        const { [cacheKey]: _, ...rest } = prev;
        return rest;
      });
      await loadSummary();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to reject remaining proposals',
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // v3.09 - Apply a decision bundle (LOCAL only — stored for batch commit)
  const handleApplyBundle = (bundle: DecisionBundle, catKey: string, subKey: string) => {
    const selectedId = bundleSelections[bundle.groupKey];
    if (!selectedId) return;

    const newDecisions: Record<string, LocalDecision> = { ...pendingDecisions };
    bundle.alternatives.forEach(alt => {
      if (alt.id === selectedId) {
        newDecisions[alt.id] = { action: 'approve', bundleGroupKey: bundle.groupKey };
      } else {
        newDecisions[alt.id] = { action: 'reject', supersede: true, bundleGroupKey: bundle.groupKey, reason: `Alternative selected: ${selectedId}` };
      }
    });
    setPendingDecisions(newDecisions);
    const selected = bundle.alternatives.find(a => a.id === selectedId);
    setSnackbar({
      open: true,
      message: `Marked: ${selected?.proposalType || 'Selected alternative'} (applied on Continue Setup)`,
      severity: 'success',
    });
  };

  // v3.09 - Approve a single standalone proposal (LOCAL only — stored for batch commit)
  const handleApproveStandalone = (proposal: Proposal) => {
    setPendingDecisions(prev => ({
      ...prev,
      [proposal.id]: { action: 'approve' },
    }));
    setSnackbar({
      open: true,
      message: `Marked as approved: ${proposal.proposalType || proposal.title}`,
      severity: 'success',
    });
  };

  // v3.09 - Reject a single standalone proposal (LOCAL only — stored for batch commit)
  const handleRejectStandalone = (proposal: Proposal) => {
    setPendingDecisions(prev => ({
      ...prev,
      [proposal.id]: { action: 'reject', reason: 'Rejected by user' },
    }));
    setSnackbar({
      open: true,
      message: `Marked as rejected: ${proposal.proposalType || proposal.title}`,
      severity: 'success',
    });
  };

  // v3.09 - Undo a local decision (removes from pendingDecisions)
  const undoDecision = (proposalId: string) => {
    setPendingDecisions(prev => {
      const next = { ...prev };
      const decision = next[proposalId];
      if (decision?.bundleGroupKey) {
        // Undo entire bundle (all alternatives in same group)
        Object.keys(next).forEach(id => {
          if (next[id]?.bundleGroupKey === decision.bundleGroupKey) {
            delete next[id];
          }
        });
      } else {
        delete next[proposalId];
      }
      return next;
    });
    setSnackbar({ open: true, message: 'Decision undone', severity: 'info' });
  };

  // v3.06 - Open strategy override dialog
  const handleChangeStrategy = async (proposalId: string, currentStrategy: string, catKey: string, subKey: string) => {
    setOverrideDialog({
      open: true, proposalId, catKey, subKey,
      currentStrategy, alternatives: [], loading: true,
    });
    try {
      const data = await proposalService.getAlternatives(proposalId);
      setOverrideDialog(prev => ({
        ...prev,
        alternatives: data.alternatives || [],
        loading: false,
      }));
    } catch (err: any) {
      setOverrideDialog(prev => ({ ...prev, loading: false }));
      setSnackbar({ open: true, message: err?.message || 'Failed to load alternatives', severity: 'error' });
    }
  };

  // v3.06 - Perform strategy override
  const handleOverrideConfirm = async (newStrategy: string) => {
    const { proposalId, catKey, subKey } = overrideDialog;
    setOverrideDialog(prev => ({ ...prev, loading: true }));
    try {
      await proposalService.overrideStrategy(proposalId, newStrategy);
      setOverrideDialog({ open: false, proposalId: '', catKey: '', subKey: '', currentStrategy: '', alternatives: [], loading: false });
      setSnackbar({ open: true, message: `Strategy changed to: ${newStrategy}`, severity: 'success' });
      // v3.07 - Force reload subcategory data after override
      const currentFilter = subcategoryFilter[`${catKey}:${subKey}`] || 'pending';
      loadSubcategory(catKey, subKey, true, currentFilter);
      await loadSummary();
    } catch (err: any) {
      setOverrideDialog(prev => ({ ...prev, loading: false }));
      setSnackbar({ open: true, message: err?.message || 'Failed to change strategy', severity: 'error' });
    }
  };

  // v3.09 - Continue setup: batch commit all local decisions, then resume pipeline
  const handleContinueSetup = async () => {
    setResuming(true);
    try {
      // Phase 1: Commit all pending local decisions to server
      const decisions = { ...pendingDecisions };
      const decisionEntries = Object.entries(decisions);

      if (decisionEntries.length > 0) {
        // Group decisions by type
        const bundleGroups: Record<string, { selectedId: string; groupKey: string }> = {};
        const standaloneApprovals: string[] = [];
        const standaloneRejections: string[] = [];

        decisionEntries.forEach(([id, decision]) => {
          if (decision.bundleGroupKey) {
            // Bundle decisions — track which is the selected one per group
            if (!decision.supersede) {
              bundleGroups[decision.bundleGroupKey] = { selectedId: id, groupKey: decision.bundleGroupKey };
            }
          } else if (decision.action === 'approve') {
            standaloneApprovals.push(id);
          } else if (decision.action === 'reject') {
            standaloneRejections.push(id);
          }
        });

        // Send bundle decisions (one API call per group using apply_bundle)
        for (const { selectedId, groupKey } of Object.values(bundleGroups)) {
          await proposalService.bulkUpdate({
            filter_type: 'groupKey',
            filter_value: groupKey,
            action: 'apply_bundle',
            selected_id: selectedId,
          });
        }

        // Send standalone approvals (chunk if >500 IDs to avoid URL limits)
        const CHUNK_SIZE = 500;
        for (let i = 0; i < standaloneApprovals.length; i += CHUNK_SIZE) {
          const chunk = standaloneApprovals.slice(i, i + CHUNK_SIZE);
          await proposalService.bulkUpdate({
            filter_type: 'ids',
            filter_value: chunk.join(','),
            action: 'approve',
          });
        }

        // Send standalone rejections
        for (let i = 0; i < standaloneRejections.length; i += CHUNK_SIZE) {
          const chunk = standaloneRejections.slice(i, i + CHUNK_SIZE);
          await proposalService.bulkUpdate({
            filter_type: 'ids',
            filter_value: chunk.join(','),
            action: 'reject',
            reason: 'Rejected by user',
          });
        }

        // v3.10 - Do NOT clear pendingDecisions here — wait until resume succeeds
        // (previously cleared here, causing lost selections if resume fails)
      }

      // Phase 2: Resume pipeline
      await setupService.resume();
      // v3.10 - Only clear local decisions AFTER both phases succeed
      setPendingDecisions({});
      onResume();
    } catch (err: any) {
      // v3.10 - Decisions are NOT cleared on error (moved clear to after success)
      // so user can safely retry without re-selecting everything
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to complete setup. Your selections are preserved. Please try again.',
        severity: 'error',
      });
      setResuming(false);
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────

  // v3.09 - Render a decision bundle (with local decision support)
  const renderDecisionBundle = (bundle: DecisionBundle, catKey: string, subKey: string) => {
    const selectedId = bundleSelections[bundle.groupKey];

    // v3.09 - Check if bundle has been locally decided
    const bundleDecided = bundle.alternatives.every(alt => pendingDecisions[alt.id]);
    if (bundleDecided) {
      const approvedAlt = bundle.alternatives.find(alt => pendingDecisions[alt.id]?.action === 'approve');
      return (
        <Card key={bundle.groupKey} variant="outlined" sx={{ mb: 2, opacity: 0.7, bgcolor: 'success.50' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                  {bundle.column || 'Table-level'}: {bundle.sourceType} / {bundle.entityType}
                </Typography>
                {approvedAlt && (
                  <Typography variant="body2" color="text.secondary">
                    Selected: <strong>{approvedAlt.proposalType || approvedAlt.operation || approvedAlt.title}</strong>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Chip label="Applied" size="small" color="success" variant="filled" />
                <Button size="small" onClick={() => undoDecision(bundle.alternatives[0].id)}>
                  Undo
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card key={bundle.groupKey} variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            {bundle.column || 'Table-level'}, {bundle.sourceType} / {bundle.entityType}
          </Typography>
          <RadioGroup
            value={selectedId || ''}
            onChange={(e) =>
              setBundleSelections((prev) => ({ ...prev, [bundle.groupKey]: e.target.value }))
            }
          >
            {bundle.alternatives.map((alt) => (
              <FormControlLabel
                key={alt.id}
                value={alt.id}
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {alt.proposalType || alt.operation || alt.title}
                    </Typography>
                    {alt.isRecommended && (
                      <Chip label="Recommended" size="small" color="primary" variant="outlined" />
                    )}
                    <Chip
                      label={alt.riskLevel || 'medium'}
                      size="small"
                      color={riskColors[alt.riskLevel || 'medium'] || 'default'}
                      variant="outlined"
                    />
                    {alt.confidence !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(alt.confidence * 100)}%
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {/* Show explanation for selected alternative */}
          {selectedId && (() => {
            const selected = bundle.alternatives.find(a => a.id === selectedId);
            if (!selected) return null;
            const explanation = selected.laymanExplanation || selected.explanation;
            return (
              <>
                {explanation ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4, fontStyle: 'italic' }}>
                    {explanation}
                  </Typography>
                ) : null}
                <ImpactLine proposal={selected} indent />
              </>
            );
          })()}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleApplyBundle(bundle, catKey, subKey)}
              disabled={!selectedId}
              startIcon={<ThumbUpIcon />}
            >
              Apply
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // v3.09 - Render a standalone proposal card (with local decision support)
  const renderStandaloneCard = (proposal: Proposal, catKey: string, subKey: string) => {
    const explanation = proposal.laymanExplanation || proposal.explanation;
    const localDecision = pendingDecisions[proposal.id];

    // v3.09 - Show decided state if locally decided
    if (localDecision && !localDecision.supersede) {
      const isApproved = localDecision.action === 'approve';
      return (
        <Card key={proposal.id} variant="outlined" sx={{ mb: 1, opacity: 0.7, bgcolor: isApproved ? 'success.50' : 'error.50' }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.secondary' }}>
                  {proposal.proposalType || proposal.operation || proposal.title}
                  {proposal.affectedColumn && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {': '}{proposal.affectedColumn}
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Chip
                  label={isApproved ? 'Approved' : 'Rejected'}
                  size="small"
                  color={isApproved ? 'success' : 'error'}
                  variant="filled"
                />
                <Button size="small" onClick={() => undoDecision(proposal.id)}>
                  Undo
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    }

    // v3.16 - SAP US12561297B2 Escape: needs_review proposals — adversarial Generator-Critic loop
    // exhausted MAX_RETRIES with reviewer confidence < CONFIDENCE_THRESHOLD (70).
    // Render a distinct amber card exposing generator instruction + reviewer critique so the human
    // reviewer can make an informed Apply (approve) or Skip (reject) decision.
    // See: services/pipeline-runner/discovery/adversarial_reviewer.py
    if (proposal.status === 'needs_review') {
      const aiDecision = proposal.aiDecision ?? {};
      const instruction = aiDecision.instruction;
      const reviewerConfidence = aiDecision.confidence;
      const concerns = aiDecision.concerns ?? [];
      const justification = aiDecision.justification ?? '';

      return (
        <Card key={proposal.id} variant="outlined" sx={{ mb: 1, borderColor: 'warning.main', borderWidth: 1.5 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>

            {/* Header row: proposal name + AI Review Needed badge + confidence chip */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ flex: 1, mr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                  <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                  <Chip
                    label="AI Review Needed"
                    size="small"
                    color="warning"
                    variant="filled"
                    sx={{ fontSize: '0.68rem', height: 20 }}
                  />
                  {reviewerConfidence !== undefined && (
                    <Chip
                      label={`Confidence: ${reviewerConfidence}%`}
                      size="small"
                      color={reviewerConfidence >= 50 ? 'warning' : 'error'}
                      variant="outlined"
                      sx={{ fontSize: '0.68rem', height: 20 }}
                    />
                  )}
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {proposal.proposalType || instruction?.operation || proposal.title}
                  {proposal.affectedColumn && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {': '}{proposal.affectedColumn}
                    </Typography>
                  )}
                </Typography>
                <ImpactLine proposal={proposal} />
              </Box>
            </Box>

            {/* Generator instruction context box */}
            {instruction && (
              <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  AI Proposed Instruction
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Operation:</strong> {instruction.operation}
                </Typography>
                {instruction.target_columns?.length > 0 && (
                  <Typography variant="caption" display="block">
                    <strong>Columns:</strong> {instruction.target_columns.join(', ')}
                  </Typography>
                )}
                {instruction.rationale && (
                  <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', mt: 0.25, color: 'text.secondary' }}>
                    {instruction.rationale}
                  </Typography>
                )}
              </Box>
            )}

            {/* Reviewer critique box: justification + concerns */}
            {(concerns.length > 0 || justification) && (
              <Box sx={{ bgcolor: 'warning.light', borderRadius: 1, p: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Reviewer Concerns
                </Typography>
                {justification && (
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    {justification}
                  </Typography>
                )}
                {concerns.slice(0, 3).map((concern: string, i: number) => (
                  <Typography key={i} variant="caption" display="block" sx={{ color: 'warning.dark' }}>
                    • {concern}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Apply / Skip action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
              <Tooltip title="Apply this instruction: it will be used in the transform pipeline">
                <Button size="small" color="success" variant="outlined" onClick={() => handleApproveStandalone(proposal)}>
                  Apply
                </Button>
              </Tooltip>
              <Tooltip title="Skip this instruction: it will not be applied">
                <Button size="small" color="error" variant="outlined" onClick={() => handleRejectStandalone(proposal)}>
                  Skip
                </Button>
              </Tooltip>
            </Box>

          </CardContent>
        </Card>
      );
    }

    return (
      <Card key={proposal.id} variant="outlined" sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {proposal.proposalType || proposal.operation || proposal.title}
                {proposal.affectedColumn && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {', '}{proposal.affectedColumn}
                  </Typography>
                )}
              </Typography>
              {explanation && (
                <Typography variant="caption" color="text.secondary">
                  {explanation}
                </Typography>
              )}
              <ImpactLine proposal={proposal} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Chip
                label={proposal.riskLevel || 'medium'}
                size="small"
                color={riskColors[proposal.riskLevel || 'medium'] || 'default'}
                variant="outlined"
              />
              {/* v3.20 - Always show Change button (reverted v3.18 condition that hid it) */}
              <Tooltip title="Change to a different strategy">
                <Button
                  size="small"
                  color="info"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => handleChangeStrategy(proposal.id, proposal.proposalType || '', catKey, subKey)}
                >
                  Change
                </Button>
              </Tooltip>
              <Button
                size="small"
                color="success"
                onClick={() => handleApproveStandalone(proposal)}
              >
                Approve
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => handleRejectStandalone(proposal)}
              >
                Reject
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // v3.08 - Render a handled (approved/rejected/superseded) proposal card (read-only)
  const renderHandledCard = (proposal: Proposal) => {
    const status = proposal.status || 'unknown';
    const statusColor = status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'default';
    const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);

    return (
      <Card key={proposal.id} variant="outlined" sx={{ mb: 1, opacity: 0.85 }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {proposal.proposalType || proposal.operation || proposal.title}
                {proposal.affectedColumn && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {', '}{proposal.affectedColumn}
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Chip
                label={proposal.riskLevel || 'medium'}
                size="small"
                color={riskColors[proposal.riskLevel || 'medium'] || 'default'}
                variant="outlined"
              />
              <Chip
                label={statusLabel}
                size="small"
                color={statusColor as any}
                variant="filled"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render L3: lazy-loaded subcategory detail
  const renderSubcategoryDetail = (catKey: string, subKey: string) => {
    const displayKey = `${catKey}:${subKey}`;
    const isLoadingSub = subcategoryLoading[displayKey];
    const detail = subcategoryData[displayKey];
    const currentFilter = subcategoryFilter[displayKey] || 'pending';

    if (isLoadingSub) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, pl: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Loading proposals...</Typography>
        </Box>
      );
    }

    if (!detail) return null;

    if (detail.totalItems === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1, pl: 2 }}>
          {currentFilter === 'handled' ? 'No handled proposals in this subcategory.' : 'No pending proposals in this subcategory.'}
        </Typography>
      );
    }

    // v3.08 - Handled view: show read-only cards
    if (currentFilter === 'handled') {
      return (
        <Box sx={{ pl: 1 }}>
          {detail.standalone.length > 0 && detail.standalone.map(proposal =>
            renderHandledCard(proposal)
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ pl: 1 }}>
        {/* Decision bundles */}
        {detail.decisionBundles.length > 0 && detail.decisionBundles.map(bundle =>
          renderDecisionBundle(bundle, catKey, subKey)
        )}

        {/* Standalone pending items */}
        {detail.standalone.length > 0 && detail.standalone.map(proposal =>
          renderStandaloneCard(proposal, catKey, subKey)
        )}
      </Box>
    );
  };

  // Render L2: subcategory accordion within a category
  const renderSubcategory = (catKey: string, subKey: string, sub: SubcategorySummary) => {
    const expandKey = `${catKey}:${subKey}`;
    const isExpanded = expandedSubcategories[expandKey] ?? false;
    // v3.10 - Dynamic need-review count: subtract local decisions from server count
    const localDecidedInSub = localCountsBySubcategory[expandKey] || 0;
    const effectiveSubNeedsReview = Math.max(0, sub.needsReviewCount - localDecidedInSub);
    const hasReviewItems = effectiveSubNeedsReview > 0;
    const isAccepting = actionLoading === `accept_${catKey}:${subKey}`;
    // Items that "Accept Recommendations" won't handle (high-risk)
    const hasHighRisk = sub.highRiskCount > 0;
    // v3.08 - Current active filter for this subcategory
    const activeFilter = subcategoryFilter[expandKey] || (hasReviewItems ? 'pending' : 'handled');

    return (
      <Accordion
        key={expandKey}
        expanded={isExpanded}
        onChange={() => handleSubcategoryToggle(catKey, subKey, sub)}
        sx={{ ml: 1, '&:before': { display: 'none' } }}
        elevation={0}
        variant="outlined"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            {hasReviewItems ? (
              <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />
            ) : (
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
            )}
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {sub.label}
            </Typography>
            {/* v3.08 - Clickable chips to toggle between handled/pending view */}
            {sub.autoHandledCount > 0 && (
              <Chip
                label={`${sub.autoHandledCount} handled`}
                size="small"
                color="success"
                variant={activeFilter === 'handled' && isExpanded ? 'filled' : 'outlined'}
                sx={{ mr: 0.5, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isExpanded) {
                    handleFilterSwitch(catKey, subKey, 'handled');
                  } else {
                    // Expand and show handled
                    setSubcategoryFilter(prev => ({ ...prev, [expandKey]: 'handled' }));
                    setExpandedSubcategories(prev => ({ ...prev, [expandKey]: true }));
                    loadSubcategory(catKey, subKey, false, 'handled');
                  }
                }}
              />
            )}
            {/* v3.10 - Show dynamic need-review count (decrements on local approve/reject) */}
            {effectiveSubNeedsReview > 0 && (
              <Chip
                label={`${effectiveSubNeedsReview} need review`}
                size="small"
                color="warning"
                variant={activeFilter === 'pending' && isExpanded ? 'filled' : 'outlined'}
                sx={{ mr: 0.5, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isExpanded) {
                    handleFilterSwitch(catKey, subKey, 'pending');
                  } else {
                    // Expand and show pending
                    setSubcategoryFilter(prev => ({ ...prev, [expandKey]: 'pending' }));
                    setExpandedSubcategories(prev => ({ ...prev, [expandKey]: true }));
                    loadSubcategory(catKey, subKey, false, 'pending');
                  }
                }}
              />
            )}
            {/* v3.10 - Show "all decided" chip when local decisions bring count to 0 */}
            {sub.needsReviewCount > 0 && effectiveSubNeedsReview === 0 && (
              <Chip
                label="All decided"
                size="small"
                color="success"
                variant="outlined"
                sx={{ mr: 0.5, pointerEvents: 'none' }}
              />
            )}
            {/* v3.07/v3.10 - Accept Recommendations + Reject Remaining buttons at L2 */}
            {/* Only show when there are still undecided items */}
            {/* v3.15 - Fix #7: "All high-risk" shown as plain text (not a chip/button).
                When non-high-risk items exist, show Accept Recommendations button. Always show Reject All. */}
            {hasReviewItems && (() => {
              const nonHighRiskCount = sub.needsReviewCount - (sub.highRiskCount || 0);
              return (
                <>
                  {nonHighRiskCount > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      disabled={isAccepting || actionLoading === `sub_reject_${catKey}:${subKey}`}
                      startIcon={isAccepting ? <CircularProgress size={14} /> : <ThumbUpIcon sx={{ fontSize: 14 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcceptSubcategoryRecommendations(catKey, subKey);
                      }}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                    >
                      Accept Recommendations
                    </Button>
                  ) : (
                    // v3.15 - Fix #7: Plain text label, not a chip/button
                    <Typography variant="caption" color="error.main" sx={{ mr: 0.5, fontStyle: 'italic' }}>
                      All high-risk: review individually
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={isAccepting || actionLoading === `sub_reject_${catKey}:${subKey}`}
                    startIcon={actionLoading === `sub_reject_${catKey}:${subKey}` ? <CircularProgress size={14} /> : null}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRejectSubcategoryRemaining(catKey, subKey);
                    }}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                  >
                    Reject All
                  </Button>
                </>
              );
            })()}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* High-risk note after accepting recommendations */}
          {hasHighRisk && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {sub.highRiskCount} high-risk item{sub.highRiskCount > 1 ? 's' : ''} require{sub.highRiskCount === 1 ? 's' : ''} individual review below.
            </Alert>
          )}
          {renderSubcategoryDetail(catKey, subKey)}
        </AccordionDetails>
      </Accordion>
    );
  };

  // Render L1: category accordion
  const renderCategory = (catKey: string, cat: CategorySummary) => {
    const isExpanded = expandedCategories[catKey] ?? false;
    // v3.10 - Dynamic need-review count at category level: sum local decisions across subcategories
    const catLocalDecided = Object.entries(cat.subcategories).reduce((sum, [subKey]) => {
      return sum + (localCountsBySubcategory[`${catKey}:${subKey}`] || 0);
    }, 0);
    const effectiveCatNeedsReview = Math.max(0, cat.needsReviewCount - catLocalDecided);
    const hasReviewItems = effectiveCatNeedsReview > 0;

    return (
      <Accordion
        key={catKey}
        expanded={isExpanded}
        onChange={() =>
          setExpandedCategories((prev) => ({ ...prev, [catKey]: !prev[catKey] }))
        }
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            {hasReviewItems ? (
              <WarningAmberIcon sx={{ color: 'warning.main' }} />
            ) : (
              <CheckCircleIcon sx={{ color: 'success.main' }} />
            )}
            <Typography variant="h6" sx={{ flex: 1 }}>
              {cat.label}
            </Typography>
            <Chip
              label={`${cat.autoHandledCount} auto-handled`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ mr: 1 }}
            />
            {/* v3.10 - Dynamic need-review count at category level */}
            {effectiveCatNeedsReview > 0 && (
              <Chip
                label={`${effectiveCatNeedsReview} need review`}
                size="small"
                color="warning"
                sx={{ mr: 0.5 }}
              />
            )}
            {cat.needsReviewCount > 0 && effectiveCatNeedsReview === 0 && (
              <Chip label="All decided" size="small" color="success" variant="outlined" sx={{ mr: 0.5, pointerEvents: 'none' }} />
            )}
            {/* v3.07 - Accept All + Reject All at L1 category level */}
            {hasReviewItems && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  disabled={actionLoading === `cat_accept_${catKey}` || actionLoading === `cat_reject_${catKey}`}
                  startIcon={actionLoading === `cat_accept_${catKey}` ? <CircularProgress size={14} /> : <ThumbUpIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCategoryAcceptAll(catKey);
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                >
                  Accept All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={actionLoading === `cat_accept_${catKey}` || actionLoading === `cat_reject_${catKey}`}
                  startIcon={actionLoading === `cat_reject_${catKey}` ? <CircularProgress size={14} /> : null}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCategoryRejectAll(catKey);
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                >
                  Reject All
                </Button>
              </>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {Object.entries(cat.subcategories).map(([subKey, sub]) =>
            renderSubcategory(catKey, subKey, sub)
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading proposal summary...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={loadSummary}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!summary) return null;

  const { totalAutoHandled, totalNeedsReview, totalProposals, categories } = summary;
  // v3.09 - Account for local decisions in progress/enablement
  const effectiveNeedsReview = Math.max(0, totalNeedsReview - locallyDecidedCount);
  const effectiveHandled = totalAutoHandled + locallyDecidedCount;
  const allDecided = effectiveNeedsReview === 0;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 3 }}>
      {/* Header */}
      <Typography variant="h5" gutterBottom>
        Review Proposals
      </Typography>
      <Alert severity={allDecided ? 'success' : 'info'} sx={{ mb: 3 }}>
        We analyzed <strong>{totalProposals}</strong> data transformation proposals.{' '}
        <strong>{totalAutoHandled}</strong> were auto-handled.{' '}
        {effectiveNeedsReview > 0 ? (
          <>
            <strong>{effectiveNeedsReview}</strong> still need your decision.
            {locallyDecidedCount > 0 && <>{' '}({locallyDecidedCount} decided, pending commit.)</>}
            {' '}"Approve All Recommendations" handles low and medium risk items.
            {' '}Use "Reject All Remaining" to dismiss any you don't want.
          </>
        ) : (
          <>All proposals have been handled.{locallyDecidedCount > 0 && ' Click "Continue Setup" to commit your decisions and proceed.'}</>
        )}
      </Alert>

      {/* v3.07/v3.09 - Action bar with Approve All + Reject All + Continue Setup */}
      {/* v3.24 - ref for auto-scroll when all proposals handled */}
      <Box ref={continueSetupRef} sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {totalNeedsReview > 0 && (
          <>
            <Tooltip title="Approve all recommended low and medium risk proposals. High-risk items require individual review or Reject All.">
              <Button
                variant="contained"
                color="primary"
                onClick={handleApproveAllRecommendations}
                disabled={actionLoading === 'approve_all' || actionLoading === 'reject_all'}
                startIcon={
                  actionLoading === 'approve_all' ? <CircularProgress size={18} /> : <ThumbUpIcon />
                }
              >
                Approve All Recommendations
              </Button>
            </Tooltip>
            <Tooltip title="Reject all remaining pending proposals (including high-risk)">
              <Button
                variant="contained"
                color="error"
                onClick={handleRejectAllRemaining}
                disabled={actionLoading === 'approve_all' || actionLoading === 'reject_all'}
                startIcon={
                  actionLoading === 'reject_all' ? <CircularProgress size={18} /> : null
                }
              >
                Reject All Remaining
              </Button>
            </Tooltip>
          </>
        )}
        <Tooltip
          title={
            effectiveNeedsReview > 0
              ? `${effectiveNeedsReview} proposals still need your decision`
              : locallyDecidedCount > 0
                ? `Commit ${locallyDecidedCount} local decision${locallyDecidedCount > 1 ? 's' : ''} and continue`
                : 'All proposals handled. Ready to continue.'
          }
        >
          <span>
            <Button
              variant="contained"
              color="success"
              onClick={handleContinueSetup}
              disabled={!allDecided || resuming}
              startIcon={resuming ? <CircularProgress size={18} /> : <PlayArrowIcon />}
            >
              {resuming ? 'Committing & Starting...' : 'Continue Setup'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Progress bar */}
      {totalProposals > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {effectiveHandled} / {totalProposals} handled{locallyDecidedCount > 0 ? ` (${locallyDecidedCount} pending commit)` : ''}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(effectiveHandled / totalProposals) * 100}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Category accordions (ALL collapsed by default) */}
      {Object.entries(categories).map(([catKey, cat]) => renderCategory(catKey, cat))}

      {/* v3.06 - Strategy Override Dialog */}
      <Dialog
        open={overrideDialog.open}
        onClose={() => setOverrideDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Strategy</DialogTitle>
        <DialogContent>
          {overrideDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : overrideDialog.alternatives.length === 0 ? (
            <Typography color="text.secondary">No alternatives available.</Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current: <strong>{overrideDialog.currentStrategy}</strong>
              </Typography>
              <List dense>
                {overrideDialog.alternatives
                  .filter(alt => !alt.isCurrent && !alt.isInvalid)
                  .map(alt => (
                    <ListItemButton
                      key={alt.techniqueId}
                      onClick={() => handleOverrideConfirm(alt.displayName)}
                      disabled={overrideDialog.loading}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Chip
                          label={alt.riskLevel}
                          size="small"
                          color={riskColors[alt.riskLevel] || 'default'}
                          variant="outlined"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={alt.displayName}
                        secondary={alt.description}
                      />
                    </ListItemButton>
                  ))}
              </List>
              {overrideDialog.alternatives.some(alt => alt.isInvalid) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Some strategies are unavailable for this column's data type.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialog(prev => ({ ...prev, open: false }))}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProposalReview;
